"use client";

type OpcoesCache = {
  ttl?: number;
  force?: boolean;
  init?: RequestInit;
};

type EntradaCache = {
  expiraEm: number;
  valor: unknown;
};

const LIMITE_ENTRADAS = 50;
const cache = new Map<string, EntradaCache>();
const pendentes = new Map<string, Promise<unknown>>();
const observadoresInvalidacao = new Set<(prefixo: string) => void>();

export function observarInvalidacaoCacheCliente(observador: (prefixo: string) => void) {
  observadoresInvalidacao.add(observador);
  return () => { observadoresInvalidacao.delete(observador); };
}

export class ErroHttpCliente extends Error {
  constructor(
    mensagem: string,
    public readonly status: number,
  ) {
    super(mensagem);
  }
}

export async function buscarJsonComCache<T>(
  url: string,
  { ttl = 30_000, force = false, init }: OpcoesCache = {},
): Promise<T> {
  const metodo = (init?.method ?? "GET").toUpperCase();
  if (metodo !== "GET") throw new Error("O cache cliente aceita somente requisicoes GET.");

  const agora = Date.now();
  const entrada = cache.get(url);
  if (!force && entrada && entrada.expiraEm > agora) return entrada.valor as T;
  if (!force && pendentes.has(url)) return pendentes.get(url) as Promise<T>;

  const requisicao = fetch(url, { ...init, credentials: init?.credentials ?? "include" })
    .then(async (resposta) => {
      const dados = await resposta.json().catch(() => null);
      if (!resposta.ok) {
        const mensagem = dados && typeof dados === "object" && "erro" in dados
          ? String(dados.erro)
          : "Nao foi possivel carregar os dados.";
        throw new ErroHttpCliente(mensagem, resposta.status);
      }

      cache.delete(url);
      cache.set(url, { expiraEm: Date.now() + ttl, valor: dados });
      while (cache.size > LIMITE_ENTRADAS) {
        const primeiraChave = cache.keys().next().value;
        if (typeof primeiraChave !== "string") break;
        cache.delete(primeiraChave);
      }
      return dados as T;
    })
    .finally(() => pendentes.delete(url));

  pendentes.set(url, requisicao);
  return requisicao;
}

export function invalidarCacheCliente(prefixo = "") {
  for (const chave of cache.keys()) {
    if (!prefixo || chave.startsWith(prefixo) || chave.includes(prefixo)) cache.delete(chave);
  }
  for (const observador of observadoresInvalidacao) {
    // Uma assinatura de componente não pode impedir a invalidação/logout.
    try { observador(prefixo); } catch {}
  }
}

export function statusErroHttpCliente(erro: unknown) {
  return erro instanceof ErroHttpCliente ? erro.status : null;
}
